import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp,
  BarChart,
  DollarSign,
  Package,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Percent,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable info tooltip for stat labels / section titles
function InfoTip({ text, testid }) {
  return (
    <TooltipProvider delayDuration={100}>
      <UITooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors cursor-help inline-flex align-middle"
            aria-label="More info"
            data-testid={testid}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px] text-xs">{text}</TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
}

export default function PricingIntelligenceContent() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getToken = () => localStorage.getItem("auth_token");

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/reports/pricing-intelligence`, { headers });
      setData(res.data);
    } catch (error) {
      console.error("Failed to load pricing intelligence", error);
      toast.error("Failed to load pricing intelligence");
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const summary = data?.summary || {};
  const products = data?.products || [];

  // Sort products: most frequently overridden first, then by total revenue impact
  const sortedProducts = [...products].sort((a, b) => {
    return (b.override_percentage || 0) - (a.override_percentage || 0);
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-600" />
            Pricing Intelligence
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Override patterns, margin analysis &amp; revenue impact
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadInsights} className="h-9">
          <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
          Analyzing pricing data...
        </div>
      ) : data && products.length > 0 ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-600">Products Tracked</span>
                <InfoTip testid="pi-info-products-tracked" text="Number of products that have appeared on quotes and are being monitored." />
              </div>
              <p className="text-2xl font-bold text-blue-900">{summary.total_products || 0}</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2 mb-1">
                <Percent className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-medium text-amber-600">Override Rate</span>
                <InfoTip testid="pi-info-override-rate" text="Share of tracked products that have had at least one manual price override." />
              </div>
              <p className="text-2xl font-bold text-amber-900">
                {(summary.override_percentage || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-amber-600 mt-1">{summary.products_with_overrides || 0} of {summary.total_products || 0} products</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-green-600">Avg Price Delta</span>
                <InfoTip testid="pi-info-avg-price-delta" text="Average per-unit difference between the overridden price and the calculated price." />
              </div>
              <p className="text-2xl font-bold text-green-900">
                {summary.avg_price_delta >= 0 ? '+' : ''}{formatCurrency(summary.avg_price_delta)}
              </p>
              <p className="text-xs text-green-600 mt-1">per unit when overridden</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 mb-1">
                <BarChart className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-600">Override Revenue</span>
                <InfoTip testid="pi-info-override-revenue" text="Total revenue from quote lines where the price was manually overridden." />
              </div>
              <p className="text-2xl font-bold text-purple-900">
                {formatCurrency(summary.total_revenue_from_overrides)}
              </p>
              <p className="text-xs text-purple-600 mt-1">{summary.most_overridden_product || ""}</p>
            </div>
          </div>

          {/* Products Table */}
          <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-500" />
              Products by Override Frequency
              <InfoTip testid="pi-info-override-frequency" text="Products ranked by how often their price is manually adjusted, with margin and price-delta stats." />
            </h3>
            <div className="space-y-2">
              {sortedProducts.slice(0, 10).map((p, idx) => {
                const overridePct = p.override_percentage || 0;
                const insight = p.insight || "";
                const insightColor =
                  insight === "Often increased" ? "bg-green-100 text-green-700" :
                  insight === "Often discounted" ? "bg-red-100 text-red-700" :
                  insight === "Frequently overridden" ? "bg-amber-100 text-amber-700" :
                  "bg-gray-100 text-gray-600";
                return (
                  <div key={idx} className="p-3 bg-white rounded border border-gray-100">
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.product_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {p.total_quotes} quotes · {p.total_overrides} overrides
                        </p>
                      </div>
                      <Badge className={`text-xs ${insightColor}`}>
                        {insight || "—"}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">Override %</span>
                        <p className="font-mono font-medium text-gray-900">{overridePct.toFixed(1)}%</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Effective Margin</span>
                        <p className="font-mono font-medium text-gray-900">{(p.avg_effective_margin || 0).toFixed(1)}%</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Avg Δ Price</span>
                        <p className={`font-mono font-medium ${p.avg_price_delta >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {p.avg_price_delta >= 0 ? '+' : ''}{formatCurrency(p.avg_price_delta)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Suggested Margin</span>
                        <p className="font-mono font-medium text-blue-700">{(p.suggested_margin || 0).toFixed(1)}%</p>
                      </div>
                    </div>
                    {p.margin_recommendation && (
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        {p.avg_price_delta >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                        {p.margin_recommendation}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No pricing override data yet</p>
          <p className="text-xs mt-1">Insights appear once quotes have had pricing adjustments</p>
        </div>
      )}
    </div>
  );
}
